/**
 * Financieel Store — Cash positie, omzet overzicht, accountant pakket
 */

import { create } from 'zustand';
import { supabase } from '../lib/supabase';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface OmzetRegel {
  month: string;
  segment: string;
  category: string | null;
  product_name: string | null;
  invoice_count: number;
  total_excl: number;
  total_incl: number;
  total_orders: number | null;
}

export interface GrootboekMapping {
  id: string;
  segment: string;
  category: string | null;
  product_name: string | null;
  grootboek_nummer: string;
  grootboek_naam: string;
  sort_order: number;
}

export interface ChecklistItem {
  id: string;
  month: string;
  item_key: string;
  item_label: string;
  is_auto: boolean;
  is_complete: boolean;
  completed_at: string | null;
  external_url: string | null;
  notes: string | null;
}

export interface KostenRegel {
  acc_id: string;
  acc_desc: string;
  amount_debit: number;
  amount_credit: number;
  net_amount: number;
  transaction_count: number;
}

export interface KostenTransactie {
  datum: string;
  omschrijving: string;
  bedrag: number;
}

export interface BankConnection {
  id: string;
  provider: string;
  institution_id?: string;
  institution_name: string;
  status: 'pending' | 'linked' | 'expired' | 'error';
  consent_expires_at: string | null;
  accounts: Array<{ account_id: string; iban: string; name: string }>;
  created_at: string;
}

export interface BankBestand {
  id: string;
  month: string;
  iban: string;
  rekening_naam: string | null;
  bestand_naam: string;
  storage_path: string;
  opening_balance: number | null;
  closing_balance: number | null;
  balance_date: string | null;
  transaction_count: number | null;
  uploaded_at: string;
}

interface FinancieelState {
  selectedMonth: string;
  isLoading: boolean;
  isRefreshing: boolean;
  isSending: boolean;
  error: string | null;

  // Cash positie
  mollieBalance: { available: number | null; pending: number; error?: string } | null;
  myposBalance: number | null;
  bankSaldi: Array<{ iban: string; naam: string; saldo: number; datum: string; bron?: 'live' | 'upload' }>;
  openstaandDebiteuren: number;

  // Omzet
  omzetData: OmzetRegel[];
  grootboekMapping: GrootboekMapping[];
  totalOrders: number;

  // Trends
  trendsData: OmzetRegel[];

  // Checklist
  checklistItems: ChecklistItem[];

  // Bank bestanden
  bankBestanden: BankBestand[];

  // Kosten (Yuki)
  kostenData: KostenRegel[];
  yukiBestand: { bestand_naam: string; uploaded_at: string } | null;
  isUploadingYuki: boolean;
  kostenTransacties: Record<string, KostenTransactie[]>; // key: `${month}|${acc_id}`

  // Bank connections (PSD2 live saldi)
  bankConnections: BankConnection[];
  isConnecting: boolean;

  // Actions
  setMonth: (month: string) => void;
  loadOverview: () => Promise<void>;
  refreshOmzet: () => Promise<void>;
  loadTrends: (months?: number) => Promise<void>;
  loadKostenTransacties: (accId: string) => Promise<void>;
  loadChecklist: () => Promise<void>;
  updateChecklistItem: (itemId: string, isComplete: boolean) => Promise<void>;
  uploadBankBestand: (file: File, rekeningNaam: string) => Promise<void>;
  uploadYukiBestand: (file: File) => Promise<void>;
  sendAccountantPakket: (email: string) => Promise<void>;
  loadBankBestanden: () => Promise<void>;
  loadBankConnections: () => Promise<void>;
  startBankAuth: (institutionId: string, institutionName: string) => Promise<string | null>;
  completeBankAuth: (code: string) => Promise<void>;
  refreshLiveBalances: () => Promise<void>;
  deleteBankConnection: (id: string) => Promise<void>;
  initMyposBalance: (balance: number) => Promise<boolean>;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function currentMonth(): string {
  const d = new Date();
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
}

async function getToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

async function apiFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const token = await getToken();
  if (!token) throw new Error('Not authenticated');
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });
}

// ─── Store ──────────────────────────────────────────────────────────────────

export const useFinancieelStore = create<FinancieelState>()((set, get) => ({
  selectedMonth: currentMonth(),
  isLoading: false,
  isRefreshing: false,
  isSending: false,
  error: null,

  mollieBalance: null,
  myposBalance: null,
  bankSaldi: [],
  openstaandDebiteuren: 0,

  omzetData: [],
  grootboekMapping: [],
  totalOrders: 0,

  trendsData: [],

  checklistItems: [],

  bankBestanden: [],

  kostenData: [],
  yukiBestand: null,
  isUploadingYuki: false,
  kostenTransacties: {},

  bankConnections: [],
  isConnecting: false,

  setMonth: (month: string) => {
    set({ selectedMonth: month });
    get().loadOverview();
    get().loadChecklist();
    get().loadBankBestanden();
  },

  loadOverview: async () => {
    const month = get().selectedMonth;
    set({ isLoading: true, error: null });

    try {
      const response = await apiFetch(`/api/financieel?action=overview&month=${month}`);
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const data = await response.json();

      set({
        mollieBalance: data.mollieBalance,
        myposBalance: data.myposBalance,
        bankSaldi: data.bankSaldi || [],
        openstaandDebiteuren: data.openstaandDebiteuren || 0,
        omzetData: data.omzetData || [],
        grootboekMapping: data.grootboekMapping || [],
        totalOrders: data.totalOrders || 0,
        kostenData: data.kostenData || [],
        yukiBestand: data.yukiBestand || null,
        isLoading: false,
      });
    } catch (error) {
      console.error('[Financieel] loadOverview error:', error);
      set({ isLoading: false, error: error instanceof Error ? error.message : 'Fout bij laden' });
    }
  },

  refreshOmzet: async () => {
    const month = get().selectedMonth;
    set({ isRefreshing: true, error: null });

    try {
      const response = await apiFetch(`/api/financieel?action=omzet&month=${month}`);
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const data = await response.json();

      set({
        omzetData: data.omzetData || [],
        grootboekMapping: data.grootboekMapping || [],
        totalOrders: data.totalOrders || 0,
        isRefreshing: false,
      });
    } catch (error) {
      console.error('[Financieel] refreshOmzet error:', error);
      set({ isRefreshing: false, error: error instanceof Error ? error.message : 'Fout bij verversen' });
    }
  },

  loadTrends: async (months = 6) => {
    try {
      const response = await apiFetch(`/api/financieel?action=trends&months=${months}`);
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const data = await response.json();

      set({ trendsData: data.trendsData || [] });
    } catch (error) {
      console.error('[Financieel] loadTrends error:', error);
    }
  },

  loadKostenTransacties: async (accId: string) => {
    const month = get().selectedMonth;
    const key = `${month}|${accId}`;
    if (get().kostenTransacties[key]) return; // al geladen
    try {
      const response = await apiFetch(
        `/api/financieel?action=kosten-transacties&month=${month}&acc=${encodeURIComponent(accId)}`
      );
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const data = await response.json();
      set((state) => ({
        kostenTransacties: { ...state.kostenTransacties, [key]: data.transacties || [] },
      }));
    } catch (error) {
      console.error('[Financieel] loadKostenTransacties error:', error);
    }
  },

  loadChecklist: async () => {
    const month = get().selectedMonth;
    try {
      const response = await apiFetch(`/api/financieel?action=checklist&month=${month}`);
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const data = await response.json();

      set({ checklistItems: data.items || [] });
    } catch (error) {
      console.error('[Financieel] loadChecklist error:', error);
    }
  },

  updateChecklistItem: async (itemId: string, isComplete: boolean) => {
    try {
      const response = await apiFetch('/api/financieel?action=checklist-update', {
        method: 'PATCH',
        body: JSON.stringify({ item_id: itemId, is_complete: isComplete }),
      });
      if (!response.ok) throw new Error('Update mislukt');

      // Update local state
      set({
        checklistItems: get().checklistItems.map(item =>
          item.id === itemId
            ? { ...item, is_complete: isComplete, completed_at: isComplete ? new Date().toISOString() : null }
            : item
        ),
      });
    } catch (error) {
      console.error('[Financieel] updateChecklistItem error:', error);
    }
  },

  uploadBankBestand: async (file: File, rekeningNaam: string) => {
    const month = get().selectedMonth;
    set({ error: null });

    try {
      // Parse the file client-side first for metadata
      const xmlContent = await file.text();

      // Simple XML parsing for IBAN and balances
      const parser = new DOMParser();
      const doc = parser.parseFromString(xmlContent, 'text/xml');
      const getEl = (parent: Element, name: string) => {
        const els = parent.getElementsByTagNameNS('*', name);
        return els.length > 0 ? els[0]!.textContent?.trim() || null : null;
      };

      const stmts = Array.from(doc.getElementsByTagNameNS('*', 'Stmt'));
      const stmt = stmts[0];
      const iban = stmt ? getEl(stmt, 'IBAN') || 'Onbekend' : 'Onbekend';

      // Extract balances
      let openingBalance: number | null = null;
      let closingBalance: number | null = null;
      let balanceDate: string | null = null;

      if (stmt) {
        const bals = Array.from(stmt.getElementsByTagNameNS('*', 'Bal'));
        for (const bal of bals) {
          const code = getEl(bal, 'Cd');
          const amtEl = bal.getElementsByTagNameNS('*', 'Amt')[0];
          const amount = amtEl ? parseFloat(amtEl.textContent || '0') : 0;
          const cdtDbt = getEl(bal, 'CdtDbtInd');
          const signed = cdtDbt === 'DBIT' ? -amount : amount;
          const dtEl = bal.getElementsByTagNameNS('*', 'Dt')[0];
          const date = dtEl ? getEl(dtEl, 'Dt') : null;

          if (code === 'OPBD') openingBalance = signed;
          if (code === 'CLBD') {
            closingBalance = signed;
            balanceDate = date;
          }
        }
      }

      const ntryCount = stmt ? stmt.getElementsByTagNameNS('*', 'Ntry').length : 0;

      const response = await apiFetch('/api/financieel-upload', {
        method: 'POST',
        body: JSON.stringify({
          xml_content: xmlContent,
          filename: file.name,
          month,
          rekening_naam: rekeningNaam,
          iban,
          opening_balance: openingBalance,
          closing_balance: closingBalance,
          balance_date: balanceDate,
          transaction_count: ntryCount,
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Upload mislukt');
      }

      // Reload data after upload
      await get().loadOverview();
      await get().loadChecklist();
      await get().loadBankBestanden();
    } catch (error) {
      console.error('[Financieel] uploadBankBestand error:', error);
      set({ error: error instanceof Error ? error.message : 'Upload mislukt' });
    }
  },

  uploadYukiBestand: async (file: File) => {
    set({ isUploadingYuki: true, error: null });

    try {
      const xmlContent = await file.text();

      // Parse XAF client-side
      const { parseXaf } = await import('../lib/parse-xaf');
      const result = parseXaf(xmlContent);

      // Flatten kosten data for all months
      const kostenPayload: any[] = [];
      for (const [month, regels] of result.kostenPerMaand) {
        for (const regel of regels) {
          kostenPayload.push({
            month,
            acc_id: regel.accID,
            acc_desc: regel.accDesc,
            amount_debit: regel.amountDebit,
            amount_credit: regel.amountCredit,
            net_amount: regel.netAmount,
            transaction_count: regel.transactionCount,
          });
        }
      }

      const response = await apiFetch('/api/financieel-yuki-upload', {
        method: 'POST',
        body: JSON.stringify({
          xml_content: xmlContent,
          filename: file.name,
          fiscal_year: result.fiscalYear,
          kosten_data: kostenPayload,
          accounts_count: result.accounts.filter(a => a.accTp === 'P').length,
          transactions_count: result.totalTransactions,
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Upload mislukt');
      }

      set({ isUploadingYuki: false });

      // Reload data after upload
      await get().loadOverview();
      await get().loadChecklist();
    } catch (error) {
      console.error('[Financieel] uploadYukiBestand error:', error);
      set({ isUploadingYuki: false, error: error instanceof Error ? error.message : 'Upload mislukt' });
    }
  },

  sendAccountantPakket: async (email: string) => {
    const month = get().selectedMonth;
    set({ isSending: true, error: null });

    try {
      const response = await apiFetch('/api/financieel-mail', {
        method: 'POST',
        body: JSON.stringify({ month, recipient_email: email }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Verzenden mislukt');
      }

      set({ isSending: false });
      await get().loadChecklist();
    } catch (error) {
      console.error('[Financieel] sendAccountantPakket error:', error);
      set({ isSending: false, error: error instanceof Error ? error.message : 'Verzenden mislukt' });
    }
  },

  loadBankBestanden: async () => {
    const month = get().selectedMonth;
    try {
      const { data } = await supabase
        .from('bank_bestanden')
        .select('*')
        .eq('month', month)
        .order('uploaded_at', { ascending: false });

      set({ bankBestanden: data || [] });
    } catch (error) {
      console.error('[Financieel] loadBankBestanden error:', error);
    }
  },

  loadBankConnections: async () => {
    try {
      const response = await apiFetch('/api/bank-connections?action=list');
      if (!response.ok) return;
      const data = await response.json();
      set({ bankConnections: data.connections || [] });
    } catch (error) {
      console.error('[Financieel] loadBankConnections error:', error);
    }
  },

  startBankAuth: async (institutionId: string, institutionName: string) => {
    set({ isConnecting: true, error: null });
    try {
      const response = await apiFetch('/api/bank-connections?action=start-auth', {
        method: 'POST',
        body: JSON.stringify({ institution_id: institutionId, institution_name: institutionName }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Koppeling starten mislukt');
      }
      const data = await response.json();
      set({ isConnecting: false });
      return data.redirect_url || null;
    } catch (error) {
      console.error('[Financieel] startBankAuth error:', error);
      set({ isConnecting: false, error: error instanceof Error ? error.message : 'Koppeling mislukt' });
      return null;
    }
  },

  completeBankAuth: async (code: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiFetch(`/api/bank-connections?action=complete-auth&code=${encodeURIComponent(code)}`);
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Autorisatie afronden mislukt');
      }
      set({ isLoading: false });
      await get().loadOverview();
      await get().loadBankConnections();
    } catch (error) {
      console.error('[Financieel] completeBankAuth error:', error);
      set({ isLoading: false, error: error instanceof Error ? error.message : 'Autorisatie mislukt' });
    }
  },

  refreshLiveBalances: async () => {
    set({ isRefreshing: true, error: null });
    try {
      const response = await apiFetch('/api/bank-connections?action=refresh-balances', {
        method: 'POST',
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Verversen mislukt');
      }
      set({ isRefreshing: false });
      await get().loadOverview();
      await get().loadBankConnections();
    } catch (error) {
      console.error('[Financieel] refreshLiveBalances error:', error);
      set({ isRefreshing: false, error: error instanceof Error ? error.message : 'Verversen mislukt' });
    }
  },

  deleteBankConnection: async (id: string) => {
    try {
      const response = await apiFetch(`/api/bank-connections?action=delete&id=${id}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Verwijderen mislukt');
      await get().loadBankConnections();
      await get().loadOverview();
    } catch (error) {
      console.error('[Financieel] deleteBankConnection error:', error);
    }
  },

  initMyposBalance: async (balance: number) => {
    try {
      const response = await apiFetch('/api/financieel?action=mypos-init-balance', {
        method: 'POST',
        body: JSON.stringify({ balance, as_of: new Date().toISOString() }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Instellen mislukt');
      }
      await get().loadOverview();
      return true;
    } catch (error) {
      console.error('[Financieel] initMyposBalance error:', error);
      set({ error: error instanceof Error ? error.message : 'MyPos beginsaldo instellen mislukt' });
      return false;
    }
  },
}));
